import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { getCssVar } from '../../../../shared/utilities/css-helper';
import { ECharts, EChartsOption } from 'echarts';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { formatLastUpdated } from 'src/app/shared/utilities/date-formatter';
import { DeviceService } from 'src/app/modules/devices/services/device.service';
import { TimezoneService } from 'src/app/shared/utilities/timezone.service';
import { UserProfileService } from 'src/app/modules/usermanagement/services/user-profile.service';
import { DeviceChartDataViewComponent, ChartDataViewData } from 'src/app/modules/devices/components/manage/device-chart/device-chart-data-view.component';

@Component({
  selector: 'app-heat-reclamation-chart',
  templateUrl: './heat-reclamation-chart.component.html',
  styleUrls: ['./heat-reclamation-chart.component.css']
})
export class HeatReclamationChartComponent implements OnInit, OnDestroy {

  @Input() deviceData: any;
  @Input() showCloseButton: boolean = false;
  @Output() closeChart = new EventEmitter<void>();
  @Output() chartHeightChanged = new EventEmitter<number>();

  selectedRange: 'day' | 'week' | 'month' | 'year' = 'day';
  selectedDateRange = { start: new Date(), end: new Date() };
  userTimezoneUtcName: string = '';

  chartOption!: EChartsOption;
  chartInstance!: ECharts;

  isLoading = false;
  hasError = false;
  noData = false;
  errorMessage = '';
  lastCommunicated: string = '';

  private themeObserver?: MutationObserver;

  private readonly SERIES_COLORS = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666',
    '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
    '#ea7ccc', '#8b5cf6', '#06b6d4', '#f97316',
    '#14b8a6', '#e11d48', '#84cc16', '#6366f1',
    '#f43f5e', '#0ea5e9', '#a855f7', '#22c55e',
    '#d946ef', '#0891b2', '#dc2626', '#65a30d',
    '#7c3aed', '#c026d3', '#059669', '#ca8a04',
    '#2563eb', '#db2777', '#16a34a', '#9333ea'
  ];

  private readonly DEFAULT_VISIBLE_SERIES = new Set([
    'Reclamation Hot Air From Exhaust Temp',
    'Reclamation Hot Water Supply Temp',
    'Heat Utilization 1 Reheat Temp',
    'Heat Utilization 2 Reheat Temp',
    'Pump Status',
    'Flow Meter',
    'Heat Utilization 1 Outside Air Temp In',
    'Heat Utilization 2 Outside Air Temp In',
    'Outside Air Temperature',
    'Outside Humidity',
    'Outside Air Pressure'
  ]);

  private readonly BAR_SERIES = new Set([
    'Pump Status',
    'Manual Pump Command',
    'Heat Utilization 1 Manual Damper Command',
    'Heat Utilization 2 Manual Damper Command'
  ]);

  private chartSub?: Subscription;
  private userProfileSub?: Subscription;
  private legendSelected: { [name: string]: boolean } = {};
  private currentSeriesData: any[] = [];
  legendItems: { name: string; color: string; isBar: boolean; selected: boolean }[] = [];
  chartHeight = 350;
  useRightLegend = false; // true when legends > 10
  constructor(
    private dashboardService: DashboardService,
    private deviceService: DeviceService,
    private timezoneService: TimezoneService,
    private userProfileService: UserProfileService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.userProfileSub = this.userProfileService.userProfile$.subscribe(profile => {
      this.userTimezoneUtcName = profile?.timezoneUtcName || '';
      // Update date range when timezone is loaded
      const today = this.timezoneService.getNowInUserTimezone(this.userTimezoneUtcName);
      this.selectedDateRange = { start: new Date(today), end: new Date(today) };
    });

    // Initialize with empty timezone (will be updated when user profile loads)
    const today = this.timezoneService.getNowInUserTimezone('');
    this.selectedDateRange = { start: new Date(today), end: new Date(today) };

    // Empty chart so echarts container renders and onChartInit fires
    this.chartOption = {
      xAxis: { type: 'time' },
      yAxis: { type: 'value' },
      series: []
    };

    // Observe theme changes and refresh chart styling
    this.themeObserver = new MutationObserver(() => {
      this.refreshChartTheme();
    });
    this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    this.loadChartData();
  }

  ngOnDestroy(): void {
    this.chartSub?.unsubscribe();
    this.userProfileSub?.unsubscribe();
    this.themeObserver?.disconnect();
  }

  onChartInit(instance: ECharts): void {
    this.chartInstance = instance;
    instance.on('legendselectchanged', (e: any) => {
      this.legendSelected = { ...e.selected };
    });
  }

  /* ===== Date navigation ===== */

  onRangeChange(): void {
    const end = new Date(this.selectedDateRange.end);
    const start = new Date(end);
    switch (this.selectedRange) {
      case 'week': start.setDate(end.getDate() - 6); break;
      case 'month': start.setMonth(end.getMonth() - 1); break;
      case 'year': start.setFullYear(end.getFullYear() - 1); break;
    }
    this.selectedDateRange = { start, end };
    this.loadChartData();
  }

  onDateRangeChange(): void {
    this.loadChartData();
  }

  onSyncRange(): void {
    const today = new Date();
    this.selectedRange = 'day';
    this.selectedDateRange = { start: new Date(today), end: new Date(today) };
    this.loadChartData();
  }

  navigatePrevious(): void {
    const s = new Date(this.selectedDateRange.start);
    const e = new Date(this.selectedDateRange.end);
    this.shiftDates(s, e, -1);
    this.selectedDateRange = { start: s, end: e };
    this.loadChartData();
  }

  navigateNext(): void {
    const s = new Date(this.selectedDateRange.start);
    const e = new Date(this.selectedDateRange.end);
    this.shiftDates(s, e, 1);
    this.selectedDateRange = { start: s, end: e };
    this.loadChartData();
  }

  private shiftDates(s: Date, e: Date, direction: 1 | -1): void {
    switch (this.selectedRange) {
      case 'day':
        s.setDate(s.getDate() + direction);
        e.setDate(e.getDate() + direction);
        break;
      case 'week':
        s.setDate(s.getDate() + 7 * direction);
        e.setDate(e.getDate() + 7 * direction);
        break;
      case 'month':
        s.setMonth(s.getMonth() + direction);
        e.setMonth(e.getMonth() + direction);
        break;
      case 'year':
        s.setFullYear(s.getFullYear() + direction);
        e.setFullYear(e.getFullYear() + direction);
        break;
    }
  }

  /* ===== Timezone utilities ===== */

  private getTimezoneLabel(): string {
    return this.timezoneService.getUtcOffsetLabel(this.userTimezoneUtcName);
  }

  private getTimezoneAwareDateRange(): { start: Date; end: Date } {
    return {
      start: this.timezoneService.convertToUserTimezone(this.selectedDateRange.start, this.userTimezoneUtcName, 'start'),
      end: this.timezoneService.convertToUserTimezone(this.selectedDateRange.end, this.userTimezoneUtcName, 'end')
    };
  }

  private formatXAxisLabel(timestamp: number): string {
    const date = new Date(timestamp);
    if (this.selectedRange === 'day') {
      return this.timezoneService.formatDateInTimezone(date, this.userTimezoneUtcName, 'time');
    }
    return this.timezoneService.formatDateInTimezone(date, this.userTimezoneUtcName, 'date');
  }

  /* ===== API ===== */

  loadChartData(): void {
    const deviceId = this.deviceData?.id;
    if (!deviceId) return;

    // Cancel any in-flight request so stale error/next callbacks don't fire
    this.chartSub?.unsubscribe();

    this.isLoading = true;
    this.hasError = false;
    this.noData = false;

    if (this.chartInstance) {
      this.chartInstance.showLoading('default', {
        text: '',
        color: '#1890ff',
        maskColor: 'rgba(255,255,255,0.5)'
      });
    }

    const dateRange = this.getTimezoneAwareDateRange();
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);

    this.chartSub = this.dashboardService.getHeatReclamationChart(deviceId, start, end).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (this.chartInstance) this.chartInstance.hideLoading();

        try {
          this.buildChart(res);
        } catch (e) {
          console.error('buildChart error:', e);
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = 'Failed to load chart data. Please try again.';
        if (this.chartInstance) this.chartInstance.hideLoading();
        console.error('Heat reclamation chart API error:', err);
      }
    });

    // mockdata for testing without API   
    //   const mockResponse = this.getMockData();
    // this.buildChart(mockResponse);
    // this.isLoading = false;
    // if (this.chartInstance) this.chartInstance.hideLoading();
  }


  // getMockData() {
  //   const now = new Date();

  //   return [{
  //     series: [
  //       {
  //         name: 'Reclamation Hot Air From Exhaust Temp',
  //         data: [
  //           { x: new Date(now.getTime() - 5 * 3600000), y: 500 },
  //           { x: new Date(now.getTime() - 4 * 3600000), y: 600 },
  //           { x: new Date(now.getTime() - 3 * 3600000), y: 700 },
  //           { x: new Date(now.getTime() - 2 * 3600000), y: 650 },
  //           { x: new Date(now.getTime() - 1 * 3600000), y: 720 }
  //         ]
  //       },
  //       {
  //         name: 'Heat Utilization 1 Reheat Temp',
  //         data: [
  //           { x: new Date(now.getTime() - 5 * 3600000), y: 300 },
  //           { x: new Date(now.getTime() - 4 * 3600000), y: 320 },
  //           { x: new Date(now.getTime() - 3 * 3600000), y: 310 },
  //           { x: new Date(now.getTime() - 2 * 3600000), y: 290 },
  //           { x: new Date(now.getTime() - 1 * 3600000), y: 280 }
  //         ]
  //       },

  //       // ✅ BAR DATA (Status type)
  //       {
  //         name: 'Pump Status',
  //         data: [
  //           { x: new Date(now.getTime() - 5 * 3600000), y: 1 },
  //           { x: new Date(now.getTime() - 4 * 3600000), y: 0 },
  //           { x: new Date(now.getTime() - 3 * 3600000), y: 1 },
  //           { x: new Date(now.getTime() - 2 * 3600000), y: 1 },
  //           { x: new Date(now.getTime() - 1 * 3600000), y: 0 }
  //         ]
  //       },

  //       // ✅ Test dynamic value (like 10)
  //       {
  //         name: 'Manual Pump Command',
  //         data: [
  //           { x: new Date(now.getTime() - 5 * 3600000), y: 2 },
  //           { x: new Date(now.getTime() - 4 * 3600000), y: 5 },
  //           { x: new Date(now.getTime() - 3 * 3600000), y: 10 },
  //           { x: new Date(now.getTime() - 2 * 3600000), y: 7 },
  //           { x: new Date(now.getTime() - 1 * 3600000), y: 3 }
  //         ]
  //       }
  //     ]
  //   }];
  // }
  /* ===== Chart builder ===== */

  private buildChart(response: any): void {
    // Response is an array: [{ series: [...] }]

    const root = Array.isArray(response) ? response[0] : response;
    const series: any[] = root?.series || [];

    this.lastCommunicated = this.formatLastCommunicated(root?.lastCommunicated);

    // Detect empty data across all series
    const totalPoints = series.reduce((sum: number, s: any) => sum + (s.data?.length ?? 0), 0);
    if (totalPoints === 0) {
      this.noData = true;
      this.currentSeriesData = [];
      return;
    }
    this.noData = false;
    this.currentSeriesData = series;

    // Build custom HTML legend items
    const hasUserSelection = Object.keys(this.legendSelected).length > 0;
    this.legendItems = series.map((s: any, i: number) => ({
      name: s.name,
      color: this.getSeriesColor(i),
      isBar: this.BAR_SERIES.has(s.name),
      selected: hasUserSelection
        ? (this.legendSelected[s.name] ?? this.DEFAULT_VISIBLE_SERIES.has(s.name))
        : this.DEFAULT_VISIBLE_SERIES.has(s.name)
    })).sort((a, b) => a.name.localeCompare(b.name));

    // Use right-side legend panel when more than 10 legends
    this.useRightLegend = this.legendItems.length > 10;

    // Update legendSelected from legendItems
    this.legendSelected = Object.fromEntries(this.legendItems.map(i => [i.name, i.selected]));


    const seriesConfig = series.map((s: any, i: number) => {
      const isBar = this.BAR_SERIES.has(s.name);

      return {
        name: s.name,
        type: isBar ? 'bar' : 'line',
        smooth: !isBar,
        yAxisIndex: isBar ? 1 : 0,

        symbol: isBar ? 'none' : 'circle',
        symbolSize: isBar ? 0 : 4,

        barWidth: isBar ? 12 : undefined,

        data: (s.data || []).map((d: any) => [
          new Date(d.x).getTime(),
          d.y   // ✅ USE ORIGINAL VALUE (0 or 1)
        ]),

        itemStyle: {
          // Use CSS variable for series color if defined, fallback to default palette
          color: this.getSeriesColor(i)
        },

        lineStyle: isBar ? undefined : { width: 2 },

        // ✅ ADD THIS BLOCK
        label: {
          show: false
        }
      };
    });

    this.chartHeight = 350;
    this.chartHeightChanged.emit(this.chartHeight);
    // Build as a local variable — do NOT assign to this.chartOption
    // (assigning would trigger ngx-echarts [options] binding → double setOption → stale Y-axis scale)
    const option: EChartsOption = {

      title: {
        text: `${this.deviceData?.name || 'Device'}`,
        // text: `${this.deviceData?.name || 'Device'} — Heat Reclamation Chart`,
        left: 'center',
        textStyle: { color: getCssVar('--font-color', '#1f2937'), fontSize: 14, fontWeight: 'bold' }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', label: { show: false } },
        formatter: (params: any) => {
          if (!Array.isArray(params) || !params.length) return '';

          const axisTime: number = params[0].axisValue;
          const date = new Date(axisTime);
          const formattedDate = this.timezoneService.formatDateInTimezone(date, this.userTimezoneUtcName, 'datetime');
          const tzLabel = this.userTimezoneUtcName ? ` ${this.getTimezoneLabel()}` : '';
          let html = `<div style="margin-bottom:5px;font-weight:bold;">${formattedDate}${tzLabel}</div>`;

          seriesConfig.forEach((s: any) => {
            if (this.legendSelected[s.name] === false) return;

            const data = s.data as [number, number][];
            if (!data.length) return;

            const point = data.reduce((nearest, d) =>
              Math.abs(d[0] - axisTime) < Math.abs(nearest[0] - axisTime) ? d : nearest
            );

            const color = s.itemStyle.color;
            const isBar = this.BAR_SERIES.has(s.name);
            const raw = point[1];
            const val = isBar ? (raw > 0 ? 'On' : 'Off') : raw.toFixed(2);
            html += `<div style="margin-top:3px;">
              <span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background:${color};"></span>
              ${s.name}: <strong>${val}</strong>
            </div>`;
          });

          return html;
        }
      },
      legend: {
        show: !this.useRightLegend,
        bottom: -4,
        data: this.legendItems.map(i => i.name),
        selected: Object.fromEntries(this.legendItems.map(i => [i.name, i.selected])),
        textStyle: { color: getCssVar('--font-color', '#1f2937') }
      },
      grid: { top: 50, left: 45, right: 30, bottom: this.useRightLegend ? 50 : 90 },
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { type: 'slider', bottom: 5, height: 18 }
      ],
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: (value: number) => this.formatXAxisLabel(value),
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Temperature',
          position: 'left'
        },
        {
          type: 'value',
          name: 'Status',
          position: 'right',
          min: 0, max: (value: any) => {
            return value.max > 2 ? value.max : 2;
          },
          interval: 1
        }
      ],
      series: seriesConfig as any[]
    };

    if (this.chartInstance) {
      this.chartInstance.setOption(option, { notMerge: true });
      // Save the option for later theme refreshes
      this.chartOption = option;
    }
  }

  selectAllLegends(): void {
    if (!this.chartInstance) return;
    const opt: any = this.chartInstance.getOption();
    (opt['series'] as any[] || []).forEach((s: any) =>
      this.chartInstance.dispatchAction({ type: 'legendSelect', name: s.name })
    );
    this.legendItems.forEach(item => item.selected = true);
    this.legendSelected = Object.fromEntries(this.legendItems.map(i => [i.name, true]));
  }

  clearAllLegends(): void {
    if (!this.chartInstance) return;
    const opt: any = this.chartInstance.getOption();
    (opt['series'] as any[] || []).forEach((s: any) =>
      this.chartInstance.dispatchAction({ type: 'legendUnSelect', name: s.name })
    );
    this.legendItems.forEach(item => item.selected = false);
    this.legendSelected = Object.fromEntries(this.legendItems.map(i => [i.name, false]));
  }

  toggleLegendItem(name: string): void {
    if (!this.chartInstance) return;
    const item = this.legendItems.find(i => i.name === name);
    if (item) {
      item.selected = !item.selected;
      this.legendSelected[name] = item.selected;
      if (item.selected) {
        this.chartInstance.dispatchAction({ type: 'legendSelect', name });
      } else {
        this.chartInstance.dispatchAction({ type: 'legendUnSelect', name });
      }
    }
  }

  private formatLastCommunicated(date: Date | string | null | undefined): string {
    return formatLastUpdated(date, this.userTimezoneUtcName);
  }

  getSeriesColor(index: number): string {
    const varName = `--series-color-${index + 1}`;
    const fallback = this.SERIES_COLORS[index % this.SERIES_COLORS.length];
    return getCssVar(varName, fallback);
  }



  refreshChartTheme(): void {
    const fontColor = getCssVar('--font-color', '#1f2937');
    if (this.chartOption) {
      const opt = this.chartOption as any;
      // @ts-ignore
      if (opt.title) {
        opt.title.textStyle = { ...(opt.title.textStyle || {}), color: fontColor };
      }
      // @ts-ignore
      if (opt.legend) {
        opt.legend.textStyle = { color: fontColor };
      }
      // Update series colors based on CSS variables
      // @ts-ignore
      if (opt.series) {
        opt.series.forEach((s: any, i: number) => {
          s.itemStyle = s.itemStyle || {};
          s.itemStyle.color = this.getSeriesColor(i);
        });
      }
    }
    if (this.chartInstance) {
      this.chartInstance.setOption(this.chartOption, true);
    }
  }

  /* ===== View Data ===== */

  openDataView(): void {
    const deviceId = this.deviceData?.id;
    if (!deviceId) return;

    const deviceTypeName = this.deviceData?.deviceTypeName || '';
    const startDate = new Date(this.selectedDateRange.start);
    const endDate = new Date(this.selectedDateRange.end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const offset = 0;
    const limit = 10000;

    this.deviceService.getNorrelChartTableViewData(deviceId, startDate, endDate, offset, limit, this.selectedRange, deviceTypeName)
      .subscribe({
        next: (response) => {
          this.openDataViewDialog(response.chartDeviceDetails);
        },
        error: () => {
          this.openDataViewDialog();
        }
      });
  }

  private openDataViewDialog(gatewayDeviceData?: any[]): void {
    const xAxisData: string[] = [];
    const seriesData = this.currentSeriesData.map((s: any, i: number) => {
      const data = (s.data || []).map((d: any) => d.y);
      if (xAxisData.length === 0) {
        (s.data || []).forEach((d: any) => {
          xAxisData.push(new Date(d.x).toLocaleString());
        });
      }
      return {
        name: s.name,
        data,
        color: this.SERIES_COLORS[i % this.SERIES_COLORS.length]
      };
    });

    const dialogData: ChartDataViewData = {
      deviceData: this.deviceData,
      chartData: {
        xAxisData,
        seriesData
      },
      timeRange: this.selectedRange,
      ...(gatewayDeviceData && { gatewayDeviceData, isGatewayView: true })
    };

    this.dialog.open(DeviceChartDataViewComponent, {
      width: '90vw',
      maxWidth: '1400px',
      data: dialogData,
      panelClass: 'device-chart-data-view-dialog',
      disableClose: false
    });
  }
}
